
import {
  computed,
  defineComponent,
  onMounted,
  reactive,
  ref,
  toRefs,
} from 'vue'
import { FormInstance, FormRules } from 'element-plus'
import { INPUT_NAMES } from '@/widgets/WPatientEditionForm/constants/inputNames'
import { PatientType } from '@/shared/types'
import { COMMON_RULES } from '@/shared/constants'
import { patientApi } from '@/shared/api'
import { usePatient } from '@/shared/composables'
import { useRoute, useRouter } from 'vue-router'
import { editPatient } from '@/widgets/WPatientEditionForm/api/edit.api'

export default defineComponent({
  name: 'WPatientEditionForm',

  setup() {
    const route = useRoute()
    const router = useRouter()
    const { setUpdatePatient } = usePatient()

    const formInputRefs = reactive({
      surnameRef: undefined,
      nameRef: undefined,
      patronymicRef: undefined,
      dateRef: undefined,
      emailRef: undefined,
      cartNumberRef: undefined,
      phoneRef: undefined,
    })

    const patientEditionFormData: PatientType = reactive({
      id: 0,
      surname: '',
      name: '',
      patronymic: '',
      date: '',
      email: '',
      cart_number: undefined,
      phone: '',
    })

    const patientEditionValidationRules = reactive<FormRules>({
      surname: [COMMON_RULES.surnameRequired],
      name: [COMMON_RULES.nameRequired],
      date: [COMMON_RULES.dateRequired],
      email: [COMMON_RULES.emailIncorrect],
    })

    const loading = ref(false)
    const patientEditionForm = ref<FormInstance>()

    const patientId = computed(() => Number(route.params.patientId))

    const getPatientInfo = async (): Promise<void> => {
      loading.value = true

      const [error, response] = await patientApi.getPatientInfo(patientId.value)

      if (!error) {
        const patient = response.data.patient

        const dateItems = patient.date.split('.')
        const correctDate = `${dateItems[2]}-${dateItems[1]}-${dateItems[0]}`

        patientEditionFormData.id = patient.id
        patientEditionFormData.surname = patient.surname
        patientEditionFormData.name = patient.name
        patientEditionFormData.patronymic = patient.patronymic
        patientEditionFormData.date = correctDate
        patientEditionFormData.email = patient.email
        patientEditionFormData.cart_number = patient.cart_number
        patientEditionFormData.phone = patient.phone
      }

      loading.value = false
    }

    const handleChangeFocusedInput = (nextInput: string): void => {
      formInputRefs[nextInput].focus()
    }

    const handleSubmitForm = (): void => {
      if (!patientEditionForm.value) return

      patientEditionForm.value.validate(async (valid): Promise<boolean> => {
        if (valid) {
          loading.value = true

          const isPatientEdited = await editPatient(patientEditionFormData)

          if (isPatientEdited) {
            setUpdatePatient(true)

            loading.value = false

            router.push({
              name: 'PatientPage',
              params: { patientId: patientId.value },
            })
          }
        }

        loading.value = false

        return false
      })
    }

    onMounted((): void => {
      getPatientInfo()
    })

    return {
      INPUT_NAMES,
      loading,
      patientEditionForm,
      ...toRefs(formInputRefs),
      patientEditionFormData,
      patientEditionValidationRules,
      handleChangeFocusedInput,
      handleSubmitForm,
    }
  },
})
